// File: src/components/artistDashboard/chartingServices/ChartProjectionGraph.js
import React, { useMemo } from 'react';

/**
 * 6-Week Trajectory Projection Graph
 * Aligned with generateChartDraft physics:
 * - Unranked tracks stay below #50 organically
 * - Packages + debut momentum build toward a Week 5/6 peak
 */
const ChartProjectionGraph = ({ currentRank = 0, packageBoostPoints = 75, isDarkMode = true }) => {
  const weeks = [1, 2, 3, 4, 5, 6];
  const isUnranked = !currentRank || currentRank > 50 || currentRank === 0;

  // Trajectory physics calculation matching generateChartDraft mechanics
  const { baselineRanks, boostedRanks } = useMemo(() => {
    // Rank 52 represents "Unranked / Swamp" (below the bottom axis line)
    const UNRANKED_VAL = 53;

    if (isUnranked) {
      // 1. ORGANIC TRAJECTORY (Unranked)
      // Without promotion, an unranked song stays unranked below the #50 threshold
      const organic = [UNRANKED_VAL, UNRANKED_VAL, UNRANKED_VAL, UNRANKED_VAL, UNRANKED_VAL, UNRANKED_VAL];

      // 2. BOOSTED TRAJECTORY (Package + Delayed Momentum Entry)
      // W1: Package injection ($75 -> ~45 pts in W1). Climbs challenger queue, reaches doorstep.
      // W2: Breaches chart (#46), triggering Week 1 momentum (+100 pts) + remaining package pts (+30 pts).
      // W3: Week 2 momentum (+50 pts) maintains position against decay (~#38).
      // W4: Week 3 momentum (+75 pts) accelerates climb (~#31).
      // W5: Week 4 momentum (+112.5 pts) breaks into top 30 (~#24).
      // W6: Week 5 momentum (+168.75 pts) drives median peak (~#19).
      
      let entryRankW2 = 47;
      let peakRankW6 = 22;

      // Scale slightly based on package tier ($49 vs $75 vs $149)
      if (packageBoostPoints >= 149) {
        entryRankW2 = 41;
        peakRankW6 = 12;
      } else if (packageBoostPoints < 60) {
        entryRankW2 = 49;
        peakRankW6 = 28;
      }

      const boosted = [
        UNRANKED_VAL,                          // Wk 1: Building buzz in swamp/trends queue
        entryRankW2,                           // Wk 2: Chart debut
        Math.round(entryRankW2 - 7),           // Wk 3: Momentum compounding
        Math.round(entryRankW2 - 14),          // Wk 4: Mid-tier climb
        Math.round(entryRankW2 - 20),          // Wk 5: Approaching peak
        peakRankW6                            // Wk 6: 5-week momentum crest
      ];

      return { baselineRanks: organic, boostedRanks: boosted };
    } else {
      // TRACK IS ALREADY CHARTING:
      // Organic decays according to weekly decay factor (0.94 for 31-50, global -10 decay)
      let currentOrganic = currentRank;
      const organic = [];
      for (let w = 1; w <= 6; w++) {
        currentOrganic = Math.min(50, currentOrganic + (w <= 2 ? 1 : 2));
        organic.push(currentOrganic);
      }

      // Boosted: Package accelerates climb earlier towards week 5/6 peak
      const boosted = [
        Math.max(1, currentRank - 3),
        Math.max(1, currentRank - 7),
        Math.max(1, currentRank - 12),
        Math.max(1, currentRank - 18),
        Math.max(1, currentRank - 23),
        Math.max(1, currentRank - 26)
      ];

      return { baselineRanks: organic, boostedRanks: boosted };
    }
  }, [currentRank, packageBoostPoints, isUnranked]);

  // SVG layout math
  const svgWidth = 380;
  const svgHeight = 165;
  const padding = { top: 22, bottom: 28, left: 42, right: 24 };

  const plotWidth = svgWidth - padding.left - padding.right;
  const plotHeight = svgHeight - padding.top - padding.bottom;

  const getX = (index) => padding.left + (index / (weeks.length - 1)) * plotWidth;
  
  // Y coordinate mapping: #1 is at top (padding.top), #50 is at bottom (padding.top + plotHeight)
  // Ranks > 50 (like 53) render just below the #50 line in the "Unranked" gutter
  const getY = (rank) => {
    if (rank > 50) return padding.top + plotHeight + 10;
    return padding.top + ((rank - 1) / 49) * plotHeight;
  };

  // Build SVG path strings
  const baselinePath = baselineRanks
    .map((r, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(r)}`)
    .join(' ');

  const boostedPath = boostedRanks
    .map((r, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(r)}`)
    .join(' ');

  const gridLineColor = isDarkMode ? '#27272a' : '#e2e8f0';
  const labelTextColor = isDarkMode ? '#a1a1aa' : '#64748b';
  const baselineStrokeColor = isDarkMode ? '#52525b' : '#94a3b8';

  return (
    <div className={`w-full p-4 rounded-2xl border transition-colors ${
      isDarkMode 
        ? 'bg-zinc-950/80 border-zinc-800/80 text-white' 
        : 'bg-slate-50 border-gray-200 text-slate-800'
    }`}>
      {/* Legend & Header */}
      <div className="flex items-center justify-between mb-2 px-1">
        <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-zinc-400">
          6-Week Trajectory Projection
        </span>
        <div className="flex items-center gap-3 text-[9px] font-bold">
          <span className="flex items-center gap-1 text-slate-500 dark:text-zinc-400">
            <span className="w-3 h-0.5 border-t border-dashed border-slate-400 dark:border-zinc-500 inline-block" />
            Organic
          </span>
          <span className="flex items-center gap-1 text-[#ff00cc]">
            <span className="w-3 h-1 bg-[#ff00cc] rounded-full inline-block" />
            With Package
          </span>
        </div>
      </div>

      <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="w-full h-auto overflow-visible">
        {/* Horizontal Gridlines for standard ranks #1, #10, #20, #30, #40, #50 */}
        {[1, 10, 20, 30, 40, 50].map((rank) => (
          <g key={rank}>
            <line
              x1={padding.left}
              y1={getY(rank)}
              x2={svgWidth - padding.right}
              y2={getY(rank)}
              stroke={gridLineColor}
              strokeDasharray="2 2"
              strokeWidth="0.8"
            />
            <text
              x={padding.left - 6}
              y={getY(rank) + 3}
              fontSize="8"
              fill={labelTextColor}
              textAnchor="end"
              fontWeight="bold"
            >
              #{rank}
            </text>
          </g>
        ))}

        {/* Unranked bottom threshold line */}
        <text
          x={padding.left - 6}
          y={getY(53) + 3}
          fontSize="7"
          fill="#ef4444"
          textAnchor="end"
          fontWeight="900"
        >
          UNCHARTED
        </text>

        {/* X-Axis Week Labels */}
        {weeks.map((week, idx) => (
          <text
            key={week}
            x={getX(idx)}
            y={svgHeight - 4}
            fontSize="8"
            fill={labelTextColor}
            textAnchor="middle"
            fontWeight="bold"
          >
            Wk {week}
          </text>
        ))}

        {/* Baseline (Organic) Trajectory */}
        <path
          d={baselinePath}
          fill="none"
          stroke={baselineStrokeColor}
          strokeWidth="1.5"
          strokeDasharray="3 3"
        />

        {/* Boosted Trajectory */}
        <path
          d={boostedPath}
          fill="none"
          stroke="#ff00cc"
          strokeWidth="2.5"
          className="drop-shadow-[0_0_8px_rgba(255,0,204,0.6)]"
        />

        {/* Boosted Data Points & Callout Badges */}
        {boostedRanks.map((r, i) => {
          const cx = getX(i);
          const cy = getY(r);
          const isDebutingWeek = isUnranked && i === 1;
          const isPeakWeek = i === 5;

          return (
            <g key={i}>
              <circle
                cx={cx}
                cy={cy}
                r="3.5"
                fill="#ff00cc"
                stroke={isDarkMode ? '#18181b' : '#ffffff'}
                strokeWidth="1.5"
              />

              {/* Helpful annotations for Debut & Peak */}
              {isDebutingWeek && (
                <text
                  x={cx}
                  y={cy - 7}
                  fontSize="7"
                  fill="#ff00cc"
                  textAnchor="middle"
                  fontWeight="900"
                >
                  DEBUT #{r}
                </text>
              )}

              {isPeakWeek && (
                <text
                  x={cx}
                  y={cy - 7}
                  fontSize="7"
                  fill="#ff00cc"
                  textAnchor="middle"
                  fontWeight="900"
                >
                  PEAK #{r}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default ChartProjectionGraph;